import type { AxiosResponse } from 'axios';

import api from 'api/api.config';

type ApiPromise<T = unknown> = Promise<AxiosResponse<T>>;

export interface ReportDto {
  id: string | number;
  title: string;
  productId?: string | number;
  powerBiId?: string;
  description?: string;
  powerBiReportType?: string;
  powerBiTitle?: string;
  [key: string]: unknown;
}

export interface ClientReportDto {
  id: string | number;
  title: string;
  productId?: string | number;
  userAssignmentStatus?: string;
  hasDataIssue?: boolean;
  description?: string;
  [key: string]: unknown;
}

export interface ReportRequest {
  powerBiId?: string | number;
  title?: string;
  description?: string;
  productId?: string | number | null;
  [key: string]: unknown;
}

export interface PowerBiReportDto {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface ReportUserDto {
  id: string | number;
  [key: string]: unknown;
}

export interface ClientReportRequest {
  userAssignmentStatus?: string;
  hasDataIssue?: boolean;
  [key: string]: unknown;
}

export interface GetClientReportsArgs {
  userId: string;
  clientId?: string | number;
  productId?: string | number;
  sort?: string;
}

export const getClientReports = ({
  userId,
  clientId,
  productId,
  sort,
}: GetClientReportsArgs): ApiPromise<Array<ClientReportDto | ReportDto>> => {
  let url = `/${userId}`;

  if (clientId) {
    url += `/clients/${clientId}`;
  }
  url += '/reports';
  const params: string[] = [];

  if (productId) {
    params.push(`productId.equals=${productId}`);
  }
  if (sort) {
    params.push(`sort=${sort}`);
  }

  if (params.length) {
    url += `?${params.join('&')}`;
  }

  return api.get(url);
};

export interface ReportIdArgs {
  userId: string;
  reportId: string | number;
}

export const getReport = ({ userId, reportId }: ReportIdArgs): ApiPromise<ReportDto> =>
  api.get(`/${userId}/reports/${reportId}`);

export interface CreateReportArgs {
  userId: string;
  reportData: ReportRequest;
}

export const createReport = ({
  userId,
  reportData,
}: CreateReportArgs): ApiPromise<ReportDto> =>
  api.post(`/${userId}/reports`, JSON.stringify(reportData));

export interface ModifyReportArgs extends ReportIdArgs {
  reportData: ReportRequest;
}

export const modifyReport = ({
  userId,
  reportId,
  reportData,
}: ModifyReportArgs): ApiPromise<ReportDto> =>
  api.put(`/${userId}/reports/${reportId}`, JSON.stringify(reportData));

export const deleteReport = ({ userId, reportId }: ReportIdArgs): ApiPromise<void> =>
  api.delete(`/${userId}/reports/${reportId}`);

export const getPowerBiReports = ({
  userId,
}: {
  userId: string;
}): ApiPromise<PowerBiReportDto[]> =>
  api.get(`/${userId}/powerbi/reports?reportExists=false`);

export interface ReportUsersArgs extends ReportIdArgs {
  clientId: string | number;
}

export const getReportUsers = ({
  userId,
  reportId,
  clientId,
}: ReportUsersArgs): ApiPromise<ReportUserDto[]> =>
  api.get(`/${userId}/reports/${reportId}/users?clientId.equals=${clientId}`);

export interface UpdateReportAccessArgs extends ReportIdArgs {
  clientId: string | number;
  value: ClientReportRequest;
}

export const updateReportAccess = ({
  userId,
  reportId,
  clientId,
  value,
}: UpdateReportAccessArgs): ApiPromise<ClientReportDto> =>
  api.put(
    `/${userId}/clients/${clientId}/reports/${reportId}`,
    JSON.stringify(value),
  );

export interface AssignReportOnUserArgs extends ReportIdArgs {
  id: string | number;
}

export const assignReportOnUser = ({
  userId,
  reportId,
  id,
}: AssignReportOnUserArgs): ApiPromise<ReportUserDto> =>
  api.put(`/${userId}/reports/${reportId}/users/${id}`);

export const unassignReportFromUser = ({
  userId,
  reportId,
  id,
}: AssignReportOnUserArgs): ApiPromise<void> =>
  api.delete(`/${userId}/reports/${reportId}/users/${id}`);
